'use client';

import { useState } from 'react';
import { Drama, Layers, FlaskConical, RotateCcw, Check } from 'lucide-react';
import { useMasquerade } from '@/hooks/useMasquerade';
import { useTierSimulator } from '@/hooks/useTierSimulator';
import { RoleIcon } from '@/components/access-control/roles/RoleIcon';
import type { Role } from '@/lib/access-control/types';
import type { LicenseTier } from '@/lib/licensing/types';
import { TIER_FEATURES, FEATURE_KEYS, TIER_DISPLAY } from '@/lib/licensing/types';
import { getPrivilegeBadge } from '@/lib/access-control/masquerade-ui';
import { TIER_COLORS, TIER_ICONS, TIER_SELECTED } from '@/lib/licensing/tier-ui';

interface TestingConsoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ── Role Masquerade Tab ─────────────────────────────────────────────────────

function RolesTabContent() {
  const {
    isActive: isMasquerading,
    roleNames: masqueradeRoleNames,
    availableRoles,
    activateMasquerade,
    deactivateMasquerade,
    loading,
  } = useMasquerade();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const toggleRole = (roleId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  };

  const handleActivate = async () => {
    if (selectedIds.size === 0) return;
    setError(null);
    try {
      await activateMasquerade([...selectedIds]);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to activate masquerade');
    }
  };

  if (isMasquerading) {
    return (
      <div className="p-6 text-center space-y-4">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
          <Drama className="w-4 h-4 text-amber-400" />
          <span className="text-sm text-amber-200">Masquerading as: {masqueradeRoleNames.join(', ')}</span>
        </div>
        <div>
          <button
            onClick={deactivateMasquerade}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-amber-200 bg-amber-800/40 hover:bg-amber-700/50 rounded-lg transition-colors disabled:opacity-50"
          >
            End Role Masquerade
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
      {availableRoles.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-8">No roles available for masquerade</p>
      ) : (
        availableRoles.map((role: Role) => {
          const isSelected = selectedIds.has(role.id);
          const badge = getPrivilegeBadge(role.privilege_level ?? 0);
          return (
            <button
              key={role.id}
              onClick={() => toggleRole(role.id)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                isSelected
                  ? 'border-blue-500 bg-blue-950/40 ring-1 ring-blue-500/50'
                  : 'border-slate-700 bg-slate-900 hover:border-slate-600'
              }`}
            >
              <RoleIcon
                badgeUrl={role.badge_url}
                color={role.color || '#ffffff33'}
                size="sm"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-white truncate">{role.name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${badge.color}`}>{badge.label}</span>
                </div>
                {role.description && (
                  <p className="text-xs text-slate-500 truncate mt-0.5">{role.description}</p>
                )}
              </div>
              {isSelected && <Check className="w-4 h-4 text-blue-400 shrink-0" />}
            </button>
          );
        })
      )}

      {error && (
        <div className="p-3 bg-red-900/20 border border-red-700/30 rounded-lg text-sm text-red-400">{error}</div>
      )}

      {availableRoles.length > 0 && (
        <div className="pt-2 border-t border-slate-700">
          <button
            onClick={handleActivate}
            disabled={selectedIds.size === 0 || loading}
            className="w-full px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-500 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Activating...' : `Masquerade as ${selectedIds.size} role${selectedIds.size !== 1 ? 's' : ''}`}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Tier Simulator Tab ──────────────────────────────────────────────────────

const FEATURE_LABELS: Record<string, string> = {
  [FEATURE_KEYS.ENTITY_EXTRACTION]: 'Entities',
  [FEATURE_KEYS.REPORT_GENERATION]: 'Reports',
  [FEATURE_KEYS.AI_CALLER]: 'AI Caller',
  [FEATURE_KEYS.AUTO_RESPONSE]: 'Auto Response',
  [FEATURE_KEYS.DEEP_ANALYSIS]: 'Deep Analysis',
  [FEATURE_KEYS.TRAINING_MODE]: 'Training',
  [FEATURE_KEYS.KNOWLEDGE_BASE]: 'Knowledge Base',
  [FEATURE_KEYS.MAP_TOOL]: 'Map',
};

function TiersTabContent() {
  const {
    isSimulating,
    simulatedTier,
    availableTiers,
    activateSimulation,
    deactivateSimulation,
    loading,
  } = useTierSimulator();

  const [selectedTier, setSelectedTier] = useState<LicenseTier | null>(simulatedTier);
  const [error, setError] = useState<string | null>(null);

  const handleActivate = async () => {
    if (!selectedTier) return;
    setError(null);
    try {
      await activateSimulation(selectedTier);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to activate simulation');
    }
  };

  return (
    <div className="p-4 space-y-3">
      {isSimulating && (
        <div className="flex items-center justify-between p-3 rounded-lg bg-violet-500/10 border border-violet-500/30">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-violet-400" />
            <span className="text-sm text-violet-200">
              Simulating: {TIER_DISPLAY[simulatedTier!]?.name || simulatedTier}
            </span>
          </div>
          <button
            onClick={deactivateSimulation}
            disabled={loading}
            className="text-xs text-violet-300 hover:text-white transition-colors disabled:opacity-50"
          >
            End
          </button>
        </div>
      )}

      {availableTiers.map(({ value, label, description }) => {
        const isSelected = selectedTier === value;
        const features = TIER_FEATURES[value] || [];
        return (
          <button
            key={value}
            onClick={() => setSelectedTier(value)}
            className={`w-full text-left p-3 rounded-lg border transition-all ${
              isSelected ? TIER_SELECTED[value] : TIER_COLORS[value]
            } hover:brightness-110`}
          >
            <div className="flex items-center gap-2 mb-1.5">
              {TIER_ICONS[value]}
              <span className="font-medium text-sm text-white">{label}</span>
              {isSelected && <Check className="w-3.5 h-3.5 text-emerald-400 ml-auto" />}
            </div>
            <p className="text-[11px] text-slate-400 mb-2">{description}</p>
            <div className="flex flex-wrap gap-1">
              {Object.entries(FEATURE_LABELS).map(([key, featureLabel]) => (
                <span
                  key={key}
                  className={`text-[9px] px-1 py-0.5 rounded ${
                    features.includes(key as any)
                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                      : 'bg-slate-800 text-slate-500 border border-slate-700'
                  }`}
                >
                  {featureLabel}
                </span>
              ))}
            </div>
          </button>
        );
      })}

      {error && (
        <div className="p-3 bg-red-900/20 border border-red-700/30 rounded-lg text-sm text-red-400">{error}</div>
      )}

      <div className="pt-2 border-t border-slate-700">
        <button
          onClick={handleActivate}
          disabled={!selectedTier || loading}
          className="w-full px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-500 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Activating...' : isSimulating ? 'Switch Tier' : 'Simulate Tier'}
        </button>
      </div>
    </div>
  );
}

// ── Testing Console Dialog ──────────────────────────────────────────────────

type ConsoleTab = 'roles' | 'tiers';

export function TestingConsoleDialog({ open, onOpenChange }: TestingConsoleDialogProps) {
  const [activeTab, setActiveTab] = useState<ConsoleTab>('roles');
  const { isActive: isMasquerading, roleNames, deactivateMasquerade } = useMasquerade();
  const { isSimulating, deactivateSimulation } = useTierSimulator();

  const hasAnyActive = isMasquerading || isSimulating;

  const handleResetAll = async () => {
    if (isMasquerading) await deactivateMasquerade();
    if (isSimulating) await deactivateSimulation();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-700">
          <div className="flex items-center gap-2.5">
            <FlaskConical className="w-5 h-5 text-cyan-400" />
            <div>
              <h2 className="text-base font-semibold text-white">Testing Console</h2>
              <p className="text-[11px] text-slate-400">Test as different roles and license tiers</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasAnyActive && (
              <button
                onClick={handleResetAll}
                className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium text-red-300 hover:text-red-200 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-md transition-colors"
              >
                <RotateCcw className="w-3 h-3" />
                Reset All
              </button>
            )}
            <button
              onClick={() => onOpenChange(false)}
              className="text-slate-400 hover:text-white transition-colors text-lg leading-none px-1"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-700">
          <button
            onClick={() => setActiveTab('roles')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'roles'
                ? 'text-amber-300 border-b-2 border-amber-400 bg-amber-500/5'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Drama className="w-4 h-4" />
            Roles
            {isMasquerading && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                Active
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('tiers')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'tiers'
                ? 'text-violet-300 border-b-2 border-violet-400 bg-violet-500/5'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Layers className="w-4 h-4" />
            Tiers
            {isSimulating && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300 border border-violet-500/30">
                Active
              </span>
            )}
          </button>
        </div>

        {/* Content */}
        {activeTab === 'roles' ? <RolesTabContent /> : <TiersTabContent />}
      </div>
    </div>
  );
}
